import React, { useState, useEffect, useRef, useCallback } from 'react';
import he from 'he';

import { setEndOfContenteditable } from 'utils/helpers';

import './MultiLineInput.styles.scss';

const encodeValue = value => {
    return he.encode(value, {
        useNamedReferences: true
    });
}

const MultiLineInput = ({ className, value, placeholder, canEdit, autoFocus, onChange, onBlur, onFocus, onKeyDown, onPaste, provideController }) => {
    const [currentValue, setCurrentValue] = useState(value);
    const elementRef = useRef(null);

    const focus = useCallback(atEnd => {
        if (elementRef.current) {
            elementRef.current.focus();
            if (atEnd && elementRef.current.textContent) {
                setEndOfContenteditable(elementRef.current);
            }
        }

    }, [elementRef]);

    const blur = useCallback(() => {
        elementRef.current && elementRef.current.blur();
    }, [elementRef]);

    const setValue = useCallback(nextValue => {
        if (elementRef.current) {
            elementRef.current.textContent = nextValue;
        }

        setCurrentValue(nextValue);
    }, [elementRef]);

    const reset = useCallback(setOriginalValue => {
        setValue(setOriginalValue ? value : '');
    }, [setValue, value]);

    const handleChange = useCallback(({ target }) => {
        const nextValue = target.textContent;
        setCurrentValue(nextValue);
        onChange && onChange(nextValue)
    }, [setCurrentValue, onChange]);

    const handleBlur = useCallback(() => {
        onBlur && onBlur(currentValue);
    }, [onBlur, currentValue]);

    const handleKeyDown = useCallback(event => {
        if (event.which === 13 && !event.shiftKey) {
            blur();
        }

        onKeyDown && onKeyDown(event);
    }, [blur, onKeyDown]);

    const handlePaste = useCallback(event => {
        event.preventDefault();

        const nextValue = (event.originalEvent || event).clipboardData.getData('text/plain');

        if (onPaste) {
            const handled = onPaste(nextValue);

            if (handled) {
                return;
            }
        }

        setCurrentValue(nextValue);
        window.document.execCommand('insertText', false, nextValue);
    }, [onPaste]);

    useEffect(() => {
        setCurrentValue(value);
    }, [value, setCurrentValue]);

    useEffect(() => {
        const ctrl = {
            focus: atEnd => {
                focus(atEnd);
            },
            blur: () => {
                blur();
            },
            reset: setOriginalValue => {
                reset(setOriginalValue);
            },
            setValue: nextValue => {
                setValue(nextValue);
            }
        };

        if (provideController) {
            if ('current' in provideController) {
                provideController.current = ctrl;
            } else {
                provideController && provideController(ctrl);
            }
        }

        if (autoFocus) {
            focus(true);
        }

        return () => {
            if (provideController) {
                if ('current' in provideController) {
                    provideController.current = null;
                } else {
                    provideController && provideController(null);
                }
            }
        }
    }, []);

    return (
        <span
            className={'multi-line-input ' + className + (currentValue === '' ? ' empty' : '')}
            contentEditable={canEdit}
            placeholder={placeholder}
            ref={elementRef}
            dangerouslySetInnerHTML={{ __html: encodeValue(value) }}
            onInput={handleChange}
            onFocus={onFocus}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
        />
    );

}

MultiLineInput.defaultProps = {
    className: '',
    value: ''
}

export default MultiLineInput;
